<template>
  <div>
    <DataTable
      :value="publisherData"
      :loading="loading"
      dataKey="id"
      scrollable
      :scrollHeight="'75vh'"
      sortField="metrics.customerCPRevenue"
      :sortOrder="-1"
    >
      <Column field="publisher.name" header="Publisher" :headerStyle="nestedHeaderStyles" frozen alignFrozen="left">
        <template #body="{ data }">
          <div class="flex items-center gap-2">
            <span>{{ data.publisher.name || data.publisher.company || '-' }}</span>
            <span v-if="data.market" :class="`fi fi-${data.market.toLowerCase()}`" class="ml-1"></span>
          </div>
        </template>
      </Column>
        <Column field="internalNote" header="Key" :headerStyle="nestedHeaderStyles" />
      <Column field="metrics.signups" header="SU's" :headerStyle="nestedHeaderStyles" >
      </Column>
      <Column field="metrics.FTD" header="FTDs" :headerStyle="nestedHeaderStyles" >
      </Column>
      <Column field="metrics.CPA" header="CPAs" :headerStyle="nestedHeaderStyles" >
      </Column>
      <Column header="Casino GGR" :headerStyle="nestedHeaderStyles" >
        <template #body="{ data }">
          {{ formatCurrency(data.metrics?.casinoGGR || 0) }}
        </template>
      </Column>
      <Column header="Publisher Revenue" :headerStyle="nestedHeaderStyles" >
        <template #body="{ data }">
          <div :class="{ 'income-cell': getPublisherTotalEarnings(data.metrics) > 0 }">
            {{ formatCurrency(getPublisherTotalEarnings(data.metrics)) }}
        </div>
         ({{ getFriendlyDealMessage(data.customerDeal) }})  
        </template>
      </Column>
      <Column header="Our Revenue" :headerStyle="nestedHeaderStyles" >
        <template #body="{ data }">
          <div :class="{ 'income-cell': getOurTotalEarnings(data.metrics) > 0 }">
            {{ formatCurrency(getOurTotalEarnings(data.metrics)) }} 
          </div>
          ({{ getFriendlyDealMessage(data.ourDeal) }})
        </template>
      </Column>
      <Column header="Total Revenue" :headerStyle="nestedHeaderStyles" >
        <template #body="{ data }">
          <div :class="{ 'income-cell': getOurTotalEarnings(data.metrics) > 0 }">
            {{ formatCurrency(getOurTotalEarnings(data.metrics) + getPublisherTotalEarnings(data.metrics)) }} 
            
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import EarningsService from '@/services/EarningsService';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { nestedHeaderStyles } from '@/global/headerStyles';
import { formatCurrency } from '@/utils/currencies';
import { getTotalEarnings, getMetricsCustomerTotal, getMetricsOurTotal } from '@/utils/earnings';
import { getFriendlyDealMessage } from '@/utils/deals';


const props = defineProps({
  advertiser: {
    type: Object,
    required: false
  },
  advertiserName: {
    type: String,
    required: false
  },
  from: {
    type: Date,
    required: true
  },
  to: {
    type: Date,
    required: true
  }
});

const publisherData = ref([]);
const loading = ref(false);

// Prefer the API-provided per-SBL clamped totals over recomputing from raw
// CP/RS sums (the recomputation lets a chargeback day on one deal eat positive
// revshare on another deal — the same cross-SBL bug the backend just fixed).
// When metrics is a single SBL row (no customerTotalRevenue field), the helpers
// fall back to the local cp + max(rs, 0) formula which is correct at that level.
const getPublisherTotalEarnings = (metrics) => getMetricsCustomerTotal(metrics);
const getOurTotalEarnings = (metrics) => getMetricsOurTotal(metrics);

const fetchData = async () => {
  if (!(props.advertiser?.advertiserId || props.advertiserName) || !props.from || !props.to) {
    return;
  }

  loading.value = true;
  let data = null;
  try {
    if (props.advertiser?.advertiserId) {
        data = await EarningsService.getAdvertiserEarningsByPublishers(props.from, props.to, props.advertiser.advertiserId);
    } else if (props.advertiserName) {
        data = await EarningsService.getAdvertiserEarningsByPublishersUsingAdvertiserName(props.from, props.to, props.advertiserName);
    }
    console.log('data', data);
    publisherData.value = Array.isArray(data.data) ? data.data : [];
    console.log('publisherData', publisherData.value);
  } catch (error) {
    console.error('Error fetching advertiser earnings by publishers:', error);
    publisherData.value = [];
  } finally {
    loading.value = false;
  }
};

watch([() => props.advertiser?.advertiserId, () => props.advertiserName, () => props.from, () => props.to], () => {
  fetchData();
}, { immediate: true });


</script>

<style scoped>
.income-cell {
  color: #22c55e;
  font-weight: 600;
}
</style>
